import React, { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    role: '',
    reason: '',
    callVolume: '',
    userType: '',
    industry: '',
    useCase: '',
    referralSource: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-12">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Thank You!</h2>
              <p className="text-gray-400 mb-6">
                We've received your message and will get back to you within 24 hours.
              </p>
              <a href="/" className="text-teal-400 hover:underline">
                Return to Homepage
              </a>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">Contact Us</h1>
            <p className="text-gray-400">
              Thank you for taking the time to contact us. We look forward to connecting with you soon.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-[#0d1117] border border-gray-800 rounded-2xl p-8">
            {/* Personal Information */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-teal-400/20 rounded text-teal-400 text-sm flex items-center justify-center">1</span>
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Email * (Business/Work Email)</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Project Type *</label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    <option value="">Select project type</option>
                    <option value="business">For my business</option>
                    <option value="team">For my team</option>
                    <option value="client">For a client (agency/consultant)</option>
                    <option value="personal">Personal / learning project</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-300 text-sm mb-2">Your Role *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    <option value="">Select your role</option>
                    <option value="owner">Business Owner / Founder</option>
                    <option value="cxo">CXO / Leadership (CEO, COO, CTO, VP, Director)</option>
                    <option value="operations">Operations / Sales / Marketing</option>
                    <option value="product">Product / Engineering</option>
                    <option value="support">Customer Support / Call Center</option>
                    <option value="agency">Agency / Consultant</option>
                    <option value="student">Student / Academic</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Reason for Contact */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-teal-400/20 rounded text-teal-400 text-sm flex items-center justify-center">2</span>
                Reason for Contact
              </h3>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Reason to contact us *</label>
                <select
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                >
                  <option value="">Select a reason</option>
                  <option value="pricing">I have questions regarding pricing</option>
                  <option value="product">I use AIConneqtors and have questions about the product</option>
                  <option value="whitelabel">I want to white label AIConneqtors</option>
                  <option value="affiliate">I want to become an Affiliate Partner</option>
                  <option value="enterprise">Enterprise inquiry</option>
                </select>
              </div>
            </div>

            {/* Usage Information */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-teal-400/20 rounded text-teal-400 text-sm flex items-center justify-center">3</span>
                Usage Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Monthly call volume *</label>
                  <select
                    name="callVolume"
                    value={formData.callVolume}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    <option value="">Select call volume</option>
                    <option value="starter">Not sure, I am just getting started</option>
                    <option value="small">&lt;1000 minutes</option>
                    <option value="medium">1001-5000 minutes</option>
                    <option value="large">5001-20000 minutes</option>
                    <option value="enterprise">20000+ minutes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">What best describes you? *</label>
                  <select
                    name="userType"
                    value={formData.userType}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    <option value="">Select user type</option>
                    <option value="startup">Startup</option>
                    <option value="agency">Agency Owner</option>
                    <option value="freelancer">Freelancer</option>
                    <option value="business">Business Owner</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">What is your industry? *</label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                    placeholder="e.g., Healthcare, E-commerce, Finance"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">How did you hear about us? *</label>
                  <select
                    name="referralSource"
                    value={formData.referralSource}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    <option value="">Select a channel</option>
                    <option value="instagram">Instagram</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="twitter">Twitter</option>
                    <option value="google">Google Search</option>
                    <option value="bing">Bing Search</option>
                    <option value="chatgpt">ChatGPT</option>
                    <option value="referral">Referral</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-300 text-sm mb-2">Please explain a little more about your use-case *</label>
                  <textarea
                    name="useCase"
                    value={formData.useCase}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                    placeholder="Describe what you're looking to achieve with Voice AI..."
                    data-testid="contact-usecase"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-400 hover:bg-teal-500 text-black py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              data-testid="contact-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Request
                </>
              )}
            </button>

            <p className="text-gray-500 text-xs text-center mt-4">
              This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
            </p>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;
